/**
 * After-Action Report (#612) — the operator-facing copy for the AAR surface, the single source
 * for these strings (scanned by afterActionVocabulary.lock.test.ts for US/ICS drift). Every term
 * is Ontario IMS 2.0-grounded:
 *
 *   "After-Action Report" ......... Appendix A #1 (verbatim — the ARTIFACT)
 *   "after-action review" ......... §8 Demobilization (verbatim — the PROCESS; NOT banned)
 *   "Recommendation" .............. App A #1 ("make recommendations for improvement")
 *   "Debrief note" ................ App A #17 ("Debriefing")
 *   "Finding" ..................... §8 ("reviewing and documenting the performance of tasks")
 *   "Deactivated" / "Reactivated" . §8 (the incident deactivates; a reactivation is the #485 antonym)
 *   "incident responders" ......... App A #1 (never "members" — that is an Area Command drift term)
 *
 * NO US/ICS vocabulary (no ICS-### form names, no "Area Commander", no PIO, no "Unit Leader") —
 * this document is FOI-disclosable and the vocabulary lock forbids that class of drift.
 */
import type { AfterActionEntryKind } from '@tsr/shared-types';

// App A #1 verbatim — the artifact title.
export const AFTER_ACTION_HEADING = 'After-Action Report';

// The process (§8) — "after-action review" is doctrine, NOT a forbidden synonym for the artifact.
export const AFTER_ACTION_REVIEW_HEADING =
  'After-action review — findings, recommendations & debrief notes';

export const AFTER_ACTION_CAPTION =
  'The closing report of the incident, completed as part of demobilization (Ontario IMS 2.0 §8).';

export const OPEN_REPORT_LABEL = 'Open after-action report';
export const ADD_ENTRY_LABEL = 'Add entry';
export const FINALIZE_LABEL = 'Finalize report';
export const EXPORT_PDF_LABEL = 'Export PDF';
export const DELIVER_PDF_LABEL = 'Export & deliver PDF';

export const DRAFT_BADGE = 'Draft';
export const FINAL_BADGE = 'Final';

export const NOT_DEACTIVATED_NOTE =
  'The After-Action Report opens when this incident is deactivated (Ontario IMS 2.0 §8).';

export const NO_REPORT_YET_NOTE =
  'No after-action report has been opened for this incident yet.';

export const EMPTY_ENTRIES_NOTE =
  'No findings, recommendations, or debrief notes yet. Planning authors these; the platform records what is written and authors nothing itself.';

// The kind labels (backend identifiers → Ontario-grounded operator labels).
export const ENTRY_KIND_LABEL: Record<AfterActionEntryKind, string> = {
  finding: 'Finding',
  recommendation: 'Recommendation',
  debrief_note: 'Debrief note',
};
export const ENTRY_KIND_HEADING: Record<AfterActionEntryKind, string> = {
  finding: 'Findings',
  recommendation: 'Recommendations',
  debrief_note: 'Debrief notes',
};

// Section headings.
export const INCIDENT_HEADING = 'Incident';
export const REACTIVATION_HEADING = 'Deactivation & reactivation';
export const OPERATIONAL_PERIODS_HEADING = 'Operational periods';
export const COMMAND_HEADING = 'Incident command';
export const DECLARATIONS_HEADING = 'Emergency declarations';
export const ROLLUPS_HEADING = 'Operational rollups';
export const SITREP_CHAIN_HEADING = 'Situation Report chain';

export const ROLLUPS_SOURCE_NOTE =
  'Read verbatim from the Final Situation Report — never re-computed here.';
export const NO_FINAL_SITREP_NOTE =
  'No Final Situation Report on file — operational rollups are not available.';

// PIN #5 — floors + the unknown companion. The report never claims everyone is accounted for.
export const FLOORS_NOTE =
  'Totals are floors (≥): counts are unknown for at least one community, or residents remain unreachable. This report does not claim everyone is accounted for.';
export const ALL_ACCOUNTED_NOTE =
  'All linked communities are accounted for (none remaining, none unreachable).';

// PIN #7 — the reactivation is a counted beat, never smoothed.
export const NO_REACTIVATION_NOTE = 'No reactivation — a single continuous incident.';
export function reactivationCyclesLabel(n: number): string {
  return `${n} reactivation cycle${n === 1 ? '' : 's'} — recorded honestly, never smoothed into one continuous incident.`;
}

// The add-entry form.
export const ENTRY_BODY_PLACEHOLDER = 'Describe an action, a function, or a gap — never a person.';
export const ENTRY_AUTHOR_PLACEHOLDER = 'Author (e.g. Situation Unit)';
export const ENTRY_SECTION_PLACEHOLDER = 'Section (optional)';

// The person-evaluation ban notice, rendered on the surface (App A #1). Mirrors the server
// constant; kept here so the vocabulary lock scans it too.
export const PERSON_EVAL_NOTICE =
  'Findings name actions, functions, and gaps — never individuals. An after-action report evaluates the actions of the incident responders (Ontario IMS 2.0, Appendix A #1); it is a disclosable record and must not name or evaluate a person.';
